import React from 'react'
import { Control, Controller } from 'react-hook-form';

interface BadgeProps {
  title?: string
  className?: string
  variant?: 'active' | 'inactive' | 'pending' | 'green' | 'yellow' | 'orange' | 'red'
  rounded?: boolean
  size?: 'sm' | 'md' | 'lg'
  children?: React.ReactNode
}

function Badge({ title, className, variant = 'inactive', rounded = false, size = 'sm', children }: BadgeProps) {
  const variants = {
    active: "",
    inactive: "bg-gray-100 text-gray-800",
    pending: "",
    green: "border border-[#B6F0D5] text-[#1F7A50] bg-[#EAFAF2]",
    yellow: "border-yellow-500 text-yellow-800 bg-yellow-50",
    orange: "border-orange-500 text-orange-800 bg-orange-50",
    red: "border-red-600 text-red-900 bg-red-100",
  };

  const icons = {
    active: "bg-green-600",
    inactive: "bg-gray-800",
    pending: "bg-yellow-800",
    green: "bg-green-600",
    yellow: "bg-yellow-600",
    orange: "bg-orange-600",
    red: "bg-red-600",
  };

  const titles = {
    active: 'Active now',
    inactive: 'Offline',
    pending: 'Inactive',
    green: '',
    yellow: '',
    orange: '',
    red: '',
  };

  const sizeClasses = {
    sm: "text-xs px-2.5 py-0.5",
    md: "text-sm px-3 py-1",
    lg: "text-base px-4 py-1.5",
  };

  const roundedClass = rounded ? "rounded-full" : "rounded-md";
  const showIcon = ['active', 'inactive', 'pending'].includes(variant);

  return (
    <span className={`font-medium inline-flex items-center ${sizeClasses[size]} ${roundedClass} border shadow-sm ${className} ${variants[variant]}`}>
      {showIcon && <span className={`w-1.5 h-1.5 rounded-full mr-1.5 ${icons[variant]}`}></span>}
      {children || title || titles[variant]}
    </span>
  );
}

interface PillsBadgeProps {
  title?: string | { label: string, value: string }[];
  className?: string;
  variant?: 'green' | 'red' | 'blue' | 'yellow' | 'orange';
  size?: 'sm' | 'md' | 'lg';
  children?: React.ReactNode;
  type?: 'badge' | 'tabs';
  control?: Control<any>
  name?: string;
  rounded?: boolean;

}

const sizeVariants = {
  sm: "text-xs px-1 py-0.25",
  md: "text-sm px-2 py-0.5",
  lg: "text-base px-3 py-1",
};

function PillsBadge({ title, className, variant = 'green', size = 'md', type = 'badge', children, control, name = '', rounded = false }: PillsBadgeProps) {
  const variants = {
    green: "border border-[#B6F0D5] text-[#1F7A50] bg-[#EAFAF2]",
    red: "border-red-600 text-red-900 bg-red-100",
    blue: "border-blue-600 text-blue-900 bg-blue-100",
    yellow: "border-yellow-500 text-yellow-800 bg-yellow-50",
    orange: "border-orange-500 text-orange-800 bg-orange-50",
    default: "border border-gray-400 text-gray-800 bg-gray-100",
  };

  const roundedClass = rounded ? "rounded-full" : "";

  if (type === 'tabs' && control) {
    return (
      <Controller
        name={name}
        control={control}
        render={({ field: { onChange, value } }) => (
          <div className="flex flex-wrap items-center gap-2">
            {Array.isArray(title) && title.map((tab, index) => (
              <span
                key={index}
                className={`cursor-pointer ${sizeVariants[size]} w-fit font-normal inline-flex items-center ${roundedClass} ${className} ${value === tab.value ? variants[variant] : variants['default']}`}
                onClick={() => onChange(tab.value)}
              >
                {tab.label}
              </span>
            ))}
          </div>
        )}
      />
    );
  }

  return (
    <span className={`${sizeVariants[size]} text-xs w-fit font-normal inline-flex items-center ${roundedClass} ${className} ${variants[variant]}`}>
      {children} {Array.isArray(title) ? title.map((t, index) => <span key={index}>{t.label}</span>) : title}
    </span>
  );
}

export { PillsBadge }

export default Badge;